import * as THREE from 'three'
import type { Hotspot } from '../types'
import { buildTerrainFlightPath, type TerrainFlightPath } from './flightPath'
import { volcanoSummit } from './terrain'

export interface TrailGuide {
  active: boolean
  start: () => void
  stop: () => void
  continue: (nextHotspotId?: string) => void
  setStops: (hotspots: Hotspot[]) => void
  update: (dt: number) => boolean
  index: number
  total: number
}

export function createTrailGuide(
  camera: THREE.PerspectiveCamera,
  hotspots: Hotspot[],
  getHeight: (x: number, z: number) => number,
  onArrive: (hotspotId: string) => void,
  onComplete: () => void,
): TrailGuide {
  const ordered = [...hotspots]
    .filter((h) => h.trailOrder != null)
    .sort((a, b) => (a.trailOrder ?? 0) - (b.trailOrder ?? 0))

  const summit = volcanoSummit()
  const lookAt = new THREE.Vector3(summit.x, summit.y, summit.z)

  /**
   * Where the camera parks above a stop.
   *
   * Resolved per leg rather than up front: the guide is built before the region
   * exists now that the map loads lazily, so sampling every stop's ground at
   * construction would ask an absent terrain for its height.
   */
  const vantage = (hotspot: Hotspot): THREE.Vector3 => {
    const [x, , z] = hotspot.position
    const y = Math.max(getHeight(x, z), 0.4)
    return new THREE.Vector3(x + 7, y + 5.5, z + 11)
  }

  let stops = [...ordered]

  let active = false
  let index = 0
  let t = 0
  let from = new THREE.Vector3()
  let lookFrom = new THREE.Vector3()
  let path: TerrainFlightPath | null = null
  let legDuration = 3
  let waiting = false
  let pausedForInteraction = false

  const start = () => {
    if (stops.length === 0) return
    active = true
    index = 0
    t = 0
    waiting = false
    pausedForInteraction = false
    from.copy(camera.position)
    prepareLeg()
  }

  const stop = () => {
    active = false
    waiting = false
    pausedForInteraction = false
  }

  const setStops = (items: Hotspot[]) => {
    stop()
    stops = [...items]
    index = 0
    t = 0
  }

  const beginLeg = () => {
    from.copy(camera.position)
    t = 0
    waiting = false
    pausedForInteraction = false
    prepareLeg()
  }

  const prepareLeg = () => {
    const stopPoint = stops[index]
    if (!stopPoint) return
    const direction = new THREE.Vector3()
    camera.getWorldDirection(direction)
    lookFrom.copy(camera.position).addScaledVector(direction, 30)
    path = buildTerrainFlightPath(from, vantage(stopPoint), getHeight, 4.5)
    legDuration = THREE.MathUtils.clamp(path.length / 46, 2.2, 4.8)
  }

  const continueJourney = (nextHotspotId?: string) => {
    if (!active || !pausedForInteraction) return
    pausedForInteraction = false
    waiting = false
    if (nextHotspotId) {
      const nextIndex = stops.findIndex(
        (stop, candidateIndex) => candidateIndex > index && stop.id === nextHotspotId,
      )
      if (nextIndex > index + 1) {
        const [chosen] = stops.splice(nextIndex, 1)
        if (chosen) stops.splice(index + 1, 0, chosen)
      }
    }
    index++
    if (index >= stops.length) {
      active = false
      onComplete()
      return
    }
    beginLeg()
  }

  const update = (dt: number): boolean => {
    if (!active || stops.length === 0) return false
    if (pausedForInteraction) return true

    const stopPoint = stops[index]!

    if (waiting) {
      return true
    }

    t = Math.min(1, t + dt / legDuration)
    const ease = t * t * (3 - 2 * t)
    if (path) camera.position.copy(path.getPointAt(ease))
    const look = new THREE.Vector3().lerpVectors(lookFrom, lookAt, ease)
    camera.lookAt(look)

    if (t >= 1) {
      waiting = true
      pausedForInteraction = true
      onArrive(stopPoint.id)
    }
    return true
  }

  return {
    get active() {
      return active
    },
    start,
    stop,
    continue: continueJourney,
    setStops,
    update,
    get index() {
      return index
    },
    get total() {
      return stops.length
    },
  }
}
